/* eslint-disable @typescript-eslint/ban-ts-comment */
import { DateTime, Session } from '@benzinga/session';
import { uniqBy } from '@benzinga/utils';

import {
  CalendarCallType,
  CalendarEvents,
  CalendarDataParameters,
  CalendarManagerEvent,
  ErrorType,
  Saga,
} from './entities';
import { EventsAction, getDateKey, CalendarDateKey } from './generateManagerParams';
import { CALENDAR_PAGESIZE, getSymbolsFromParameters } from './generateRestfulParams';

const captureMessage = async (fetchEvents: CalendarEvents[]): Promise<void> => {
  const duplicates = fetchEvents
    .filter((arrElem, index, arr) => arr.slice(index + 1).find(({ id }) => id === arrElem.id))
    .map(({ id }) => id);
  console.error(`Duplicate events in Calendar grid: ${duplicates}`); // for debugging purposes - remove once it goes beta
};

const createTimeComparator =
  (dateKey: CalendarDateKey) =>
  (a: CalendarEvents, b: CalendarEvents): -1 | 1 => {
    const time = 'time' as CalendarDateKey;
    const aDateTime = DateTime.fromISO(a[dateKey] as string, a[time] as string);
    const bDateTime = DateTime.fromISO(b[dateKey] as string, b[time] as string);
    return aDateTime > bDateTime ? -1 : 1;
  };

export const getFirstLoadEvents = (
  eventsAction: EventsAction,
  parameters: CalendarDataParameters,
  session: Session,
  prevEvents: CalendarEvents[] = [],
  payloadEvents: CalendarEvents[] = [],
  batchLoaded = false,
): CalendarEvents[] => {
  const dateKey = getDateKey(parameters.calendarType);
  switch (eventsAction) {
    case EventsAction.addFirstBatch:
      return batchLoaded ? prevEvents : payloadEvents;
    case EventsAction.addSymbols:
      return prevEvents.concat(payloadEvents).sort(createTimeComparator(dateKey)).splice(0, CALENDAR_PAGESIZE);
    case EventsAction.appendNew: {
      const lastDateIndex = prevEvents.findIndex(event => event[dateKey] === payloadEvents[0]?.[dateKey]);
      const usablePrevEvents = lastDateIndex !== -1 ? prevEvents.slice(0, lastDateIndex) : prevEvents;
      return usablePrevEvents.concat(payloadEvents).slice(0, CALENDAR_PAGESIZE);
    }
    case EventsAction.doNothing:
      return prevEvents;
    case EventsAction.prependNew:
      return payloadEvents.concat(prevEvents).slice(0, CALENDAR_PAGESIZE);
    case EventsAction.removeSymbols: {
      const symbols = getSymbolsFromParameters(parameters, session);
      return prevEvents.filter(event => symbols.has(event.ticker));
    }
    case EventsAction.replaceAll:
      return payloadEvents;
    case EventsAction.slicePost: {
      const dateStartMoment = DateTime.fromISO(parameters.dateStart);
      const lastDateIndex = prevEvents.findIndex(event => DateTime.fromISO(event[dateKey] as string) < dateStartMoment);
      return lastDateIndex !== -1 ? prevEvents.slice(0, lastDateIndex) : prevEvents;
    }
    case EventsAction.slicePre: {
      const dateEndMoment = DateTime.fromISO(parameters.dateEnd);
      const firstDateIndex = prevEvents.findIndex(event =>
        DateTime.fromISO(event[dateKey] as string).equalsOrBefore(dateEndMoment),
      );
      return firstDateIndex !== -1 ? prevEvents.slice(firstDateIndex) : [];
    }
    default:
      return payloadEvents;
  }
};

const getUniqueEventsData = (
  newEvents: CalendarEvents[],
  prevEvents: CalendarEvents[] = [],
): { onlyNewEvents: CalendarEvents[]; updatedOldEvents: CalendarEvents[] } => {
  const newEventsMap = newEvents.reduce((result, event) => {
    result.set(event.id, event);
    return result;
  }, new Map<string, CalendarEvents>());

  const updatedOldEvents = [] as CalendarEvents[];
  prevEvents.forEach(event => {
    const { id } = event;
    const newEvents = newEventsMap.get(id);
    if (newEvents) {
      updatedOldEvents.push(newEvents);
      newEventsMap.delete(id);
    } else {
      updatedOldEvents.push(event);
    }
  });
  const onlyNewEvents = Array.from(newEventsMap.values());

  return { onlyNewEvents, updatedOldEvents };
};

export const transform = (
  event: CalendarManagerEvent,
  prevEvents: CalendarEvents[] | undefined,
  session: Session,
  currentSaga?: Saga,
): { batchLoaded?: boolean; transformedEvent: CalendarManagerEvent | null } => {
  switch (event.type) {
    case CalendarCallType.ERROR:
      if (event.errorType === ErrorType.GET_EVENTS) {
        return { transformedEvent: { ...event, events: [], lastUpdatedEventsIds: [] } };
      }
      break;
    case CalendarCallType.SET_EVENTS: {
      if (currentSaga && event.sagaId === currentSaga.id) {
        const { eventsAction, newEvents, requestParameters: parameters } = event;

        const fetchEvents = getFirstLoadEvents(
          eventsAction,
          parameters,
          session,
          prevEvents,
          newEvents,
          currentSaga.batchLoaded,
        );
        const fetchEventsUniq = uniqBy(fetchEvents, 'id');

        event.events = fetchEventsUniq;
        event.lastUpdatedEventsIds = [];

        if (fetchEvents.length !== fetchEventsUniq.length) {
          captureMessage(fetchEvents);
        }
        return {
          batchLoaded: true,
          transformedEvent: { ...event, events: fetchEventsUniq, lastUpdatedEventsIds: [] },
        };
      }
      break;
    }
    case CalendarCallType.UPDATE_EVENTS: {
      const { onlyNewEvents, updatedOldEvents } = getUniqueEventsData(event.newEvents, prevEvents);
      return {
        transformedEvent: {
          ...event,
          events: [...onlyNewEvents, ...updatedOldEvents],
          lastUpdatedEventsIds: onlyNewEvents.map(e => e.id),
        },
      };
    }
  }
  return { transformedEvent: null };
};
